import React, { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { INK, badgeSx, cardDetailsSx, cardFrameSx, cardSummarySx, valuePillSx } from "./cardChrome";

/**
 * One entry of a resource list (a laser, a substrate, a camera calibration)
 * in the card idiom of the New exploration page: an icon badge, the name
 * with a one-line caption, the key facts as pills, an actions slot and, for
 * an expandable card, the details below the header. The details stay
 * mounted while collapsed, so a card can keep loading them; `onToggle`
 * receives the new open state.
 *
 * caption: [string] — the parts are separate spans joined by a middle dot;
 *          empty parts (an unknown creator or date) are left out.
 * pills:   [{ key?, label?, value, tone?, mono?, title? }]
 */
export default function ResourceCard({
  cardKey,
  icon,
  title,
  caption = [],
  pills = [],
  actions = null,
  expandable = true,
  defaultExpanded = false,
  onToggle,
  children,
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const isOpen = expandable && open;
  const captionParts = caption.filter(Boolean);

  const handleChange = (_event, next) => {
    setOpen(next);
    if (onToggle) onToggle(next);
  };
  // The actions sit inside the header, which is itself a button: their
  // clicks and focus must not toggle the card.
  const stop = (event) => event.stopPropagation();

  const header = (
    <>
      <Box aria-hidden sx={badgeSx("neutral")}>
        {icon}
      </Box>
      {/* The title keeps room for a typical name before the pills wrap. */}
      <Box sx={{ minWidth: { xs: 0, md: 260 }, flex: "1 1 auto" }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.25, color: INK, overflowWrap: "anywhere" }}>
          {title}
        </Typography>
        {captionParts.length > 0 ? (
          <Typography
            variant="caption"
            component="div"
            sx={{ lineHeight: 1.35, color: "#64748b", mt: 0.25, overflowWrap: "anywhere" }}
          >
            {captionParts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 ? <span> · </span> : null}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </Typography>
        ) : null}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flex: "0 1 auto", minWidth: 0 }}
      >
        {pills.map((pill) => (
          <Typography
            key={pill.key || pill.value}
            component="span"
            variant="body2"
            title={pill.title}
            sx={valuePillSx({ tone: pill.tone, mono: pill.mono })}
          >
            {pill.label ? (
              <Box
                component="span"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  mr: 0.5,
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: "0.8rem",
                }}
              >
                {pill.label}
              </Box>
            ) : null}
            <span>{pill.value}</span>
          </Typography>
        ))}
      </Stack>
      {actions ? (
        <Box onClick={stop} onFocus={stop} sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {actions}
        </Box>
      ) : null}
    </>
  );

  if (!expandable) {
    return (
      <Box
        data-testid={`resource-card-${cardKey}`}
        data-resource-card={cardKey}
        sx={{
          ...cardFrameSx(false),
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          minHeight: 72,
          boxSizing: "border-box",
        }}
      >
        {header}
      </Box>
    );
  }

  return (
    // The Box keeps every card "first-of-type" so MUI's shared-corner rules
    // for stacked accordions do not flatten the middle cards.
    <Box sx={{ width: "100%" }}>
      <Accordion
        expanded={isOpen}
        onChange={handleChange}
        disableGutters
        elevation={0}
        data-testid={`resource-card-${cardKey}`}
        data-resource-card={cardKey}
        sx={cardFrameSx(isOpen)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRoundedIcon />}
          aria-controls={`resource-card-${cardKey}-content`}
          id={`resource-card-${cardKey}-header`}
          data-testid={`resource-card-${cardKey}-header`}
          sx={cardSummarySx}
        >
          {header}
        </AccordionSummary>
        <AccordionDetails
          id={`resource-card-${cardKey}-content`}
          data-testid={`resource-card-${cardKey}-content`}
          sx={cardDetailsSx}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

/** The "Used by n explorations" pill of a resource; blue while in use. */
export function usagePill(count) {
  const used = Number(count) || 0;
  return {
    key: "usage",
    value: used > 0 ? `Used by ${used} exploration${used === 1 ? "" : "s"}` : "Unused",
    tone: used > 0 ? "accent" : "neutral",
  };
}

/**
 * The delete button of a resource card, disabled while explorations use
 * the resource (the server refuses the delete then too); the tooltip says
 * why.
 */
export function DeleteResourceButton({ usedBy, noun, onClick }) {
  const used = Number(usedBy) || 0;
  return (
    <Tooltip
      title={
        used > 0
          ? `Used by ${used} exploration${used === 1 ? "" : "s"} — cannot be deleted`
          : `Delete ${noun}`
      }
    >
      <span>
        <IconButton
          size="small"
          disabled={used > 0}
          onClick={onClick}
          aria-label={`delete ${noun}`}
          sx={{
            color: "#64748b",
            "&:hover": { color: "#b42318", bgcolor: "rgba(180, 35, 24, 0.06)" },
            "&.Mui-disabled": { color: "rgba(15, 23, 42, 0.16)" },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
